import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import KitchenIcon from '@mui/icons-material/Kitchen';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { addDays, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BatchCookingWizard from './BatchCookingWizard';

const API_URL = import.meta.env.VITE_API_URL;

const statusLabels = {
  draft: 'Borrador',
  ready: 'Listo',
  validated: 'Validado',
  applied: 'Aplicado',
  cooking: 'En cocina',
  completed: 'Completado',
  archived: 'Archivado',
};

const statusColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'cooking') return 'warning';
  if (status === 'archived') return 'default';
  return 'primary';
};

const BatchCookingModal = ({ open, onClose, currentWeekStart, initialMode = 'list', initialRange }) => {
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const weekStart = useMemo(
    () => startOfWeek(currentWeekStart ? new Date(currentWeekStart) : new Date(), { weekStartsOn: 1 }),
    [currentWeekStart],
  );
  const weekStartKey = format(weekStart, 'yyyy-MM-dd');
  const weekEndKey = format(addDays(weekStart, 6), 'yyyy-MM-dd');

  const defaultRange = useMemo(() => ({
    start: weekStartKey,
    end: weekEndKey,
  }), [weekStartKey, weekEndKey]);

  const fetchPlans = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals?limit=30`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudieron cargar los planes');
      setPlans(payload.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      if (initialMode === 'list') fetchPlans();
    }
  }, [open, initialMode, fetchPlans]);

  const overlapsCurrentWeek = (plan) => plan.start <= weekEndKey && plan.end >= weekStartKey;

  const { current, others, archived } = useMemo(() => {
    const active = plans.filter((plan) => plan.status !== 'archived');
    return {
      current: active.filter(overlapsCurrentWeek),
      others: active.filter((plan) => !overlapsCurrentWeek(plan)),
      archived: plans.filter((plan) => plan.status === 'archived'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, weekStartKey, weekEndKey]);

  const handleArchive = async (planId) => {
    setBusyId(planId);
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals/${planId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo archivar');
      await fetchPlans();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId('');
    }
  };

  const openPlan = (planId) => {
    onClose?.();
    navigate(`/batch-cooking/${planId}`);
  };

  const handleCreated = (planId) => {
    onClose?.();
    navigate(`/batch-cooking/${planId}`);
  };

  const renderPlanCard = (plan) => (
    <Paper key={plan.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={800}>{plan.start} – {plan.end}</Typography>
            <Chip size="small" color={statusColor(plan.status)} label={statusLabels[plan.status] || plan.status} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {plan.metrics?.prepBlocksCount || 0} preparaciones · {plan.metrics?.stockCreatedPortions || 0} raciones Fondo
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => openPlan(plan.id)}>Abrir</Button>
          {plan.status !== 'archived' && (
            <IconButton size="small" onClick={() => handleArchive(plan.id)} disabled={busyId === plan.id} title="Archivar">
              {busyId === plan.id ? <CircularProgress size={18} /> : <ArchiveIcon fontSize="small" />}
            </IconButton>
          )}
        </Stack>
      </Box>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'create' && (
          <IconButton size="small" onClick={() => setMode('list')} title="Volver">
            <ArrowBackIcon />
          </IconButton>
        )}
        <KitchenIcon color="primary" />
        {mode === 'create' ? 'Nuevo plan de batch cooking' : 'Batch cooking'}
        <Box flexGrow={1} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {mode === 'create' ? (
          <BatchCookingWizard
            initialRange={initialRange || defaultRange}
            startAtSelection={!!initialRange && initialMode === 'create'}
            source={initialMode === 'create' && initialRange ? 'ai' : 'menu-semanal'}
            onCancel={() => (initialMode === 'create' ? onClose?.() : setMode('list'))}
            onCreated={handleCreated}
          />
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setMode('create')}>
                Crear nuevo plan
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            ) : (
              <>
                <Box>
                  <Typography variant="overline" color="text.secondary">Esta semana</Typography>
                  {current.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No hay planes para la semana en curso.</Typography>
                  ) : (
                    <Stack spacing={1} mt={0.5}>{current.map(renderPlanCard)}</Stack>
                  )}
                </Box>

                {others.length > 0 && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Otros planes</Typography>
                    <Stack spacing={1} mt={0.5}>{others.map(renderPlanCard)}</Stack>
                  </Box>
                )}

                {archived.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Archivados</Typography>
                      <Stack spacing={1} mt={0.5}>{archived.map(renderPlanCard)}</Stack>
                    </Box>
                  </>
                )}

                {plans.length === 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    Aún no tienes planes de batch cooking. Crea el primero.
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BatchCookingModal;
